import clsx from 'clsx';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useLocation, useParams, useSearchParams } from 'react-router-dom';
import {
  Button,
  Flex,
  Heading,
  Separator,
  Spinner,
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
  Text,
} from '@optiaxiom/react';
import { IconArrowLeft, IconXmark } from '@optimizely/axiom-icons';
import { useDecision } from '@optimizely/react-sdk';

import { ConfirmationModal } from '@frontend/components/confirmationModal/components/ConfirmationModal';
import { Sidebar } from '@frontend/components/sidebar/Sidebar';
import { FeatureFlag } from '@frontend/contexts';
import {
  useAgentId,
  useCreateSpecializedAgent,
  useDeleteSpecializedAgent,
  useGetSpecializedAgent,
  useSpecializedAgentReducer,
  useUnusedParameters,
  useUpdateSpecializedAgent,
} from '@frontend/features/agents/hooks';
import { useInstanceId, useNamespacedNavigate } from '@frontend/hooks';
import { AppPagePadding } from '@frontend/styles';
import * as agentStyles from '@frontend/styles/form.css';
import { SpecializedAgentListItem } from '@frontend/types';
import {
  AgentParameter,
  AgentParameterType,
  SpecializedAgent,
  SpecializedAgentExecution,
} from '@frontend/types/specialized-agent';

import { agentExecutionsService } from '../../../services/agentExecutions';
import { AgentActionButtons } from '../../shared/AgentActionButtons';
import { AgentBasicFields } from '../../shared/AgentBasicFields';
import { AgentExecutionForm } from '../agent-execution/AgentExecutionForm';
import { AppCompanionTab } from '../app-companion/AppCompanionTab';
import { SpecializedAgentExecutionDetailsSidebar } from '../executions/SpecializedAgentExecutionDetailsSidebar';
import { SpecializedAgentExecutionsTab } from '../executions/SpecializedAgentExecutionsTab';

import { AgentFormInput, AgentFormOutput, VariableRow } from './components';
import * as styles from './SpecializedAgentForm.css';

export function SpecializedAgentCreationForm({
  agentListItem,
  isEmbedded = false,
  onClose,
  backHref,
  isStickyHeader = false,
}: {
  agentListItem?: SpecializedAgentListItem;
  isEmbedded?: boolean;
  onClose?: () => void;
  backHref?: string | null;
  isStickyHeader?: boolean;
}) {
  const navigate = useNamespacedNavigate();
  const location = useLocation();
  const params = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const { instanceId } = useInstanceId();
  const [app_companion_ff] = useDecision('app_companion');

  const hasAppCompanionFF = useMemo(() => app_companion_ff?.enabled ?? false, [app_companion_ff]);

  // Get agent ID from URL params
  const agentIdFromUrl = params.agentId;
  const executionIdFromUrl = searchParams.get('executionId');
  const isCopyMode = location.pathname.includes('/copy');
  const isEditing = !!agentIdFromUrl && !isCopyMode; // If there's an agentId in URL and not copy mode, we're editing

  const activeTab = useMemo((): string => {
    const tabFromQuery = searchParams.get('tab');
    switch (tabFromQuery) {
      case 'app-companion':
        return 'app-companion';
      case 'execution':
        return 'execution';
      default:
        return 'form';
    }
  }, [searchParams]);

  const goBackToBackHref = useCallback(() => {
    navigate(backHref ? backHref : '/agents');
  }, [backHref, navigate]);

  // Accept agent from either prop or navigation state, but prioritize URL-based loading
  const navigationState = location.state as
    | { specializedAgentItem?: SpecializedAgentListItem }
    | undefined;
  const currentAgentListItem = agentListItem || navigationState?.specializedAgentItem || undefined;

  // Build agent details URL from URL param if available, otherwise use the one from navigation state
  const agentDetailsUrl = agentIdFromUrl
    ? `/hypatia/api/v1/${instanceId}/agents/specialized/${agentIdFromUrl}`
    : currentAgentListItem?.links.self;

  const { agent: detailedAgent, isPending: isAgentLoading } = useGetSpecializedAgent(
    agentDetailsUrl || ''
  );

  const [formData, dispatch] = useSpecializedAgentReducer(detailedAgent || undefined);

  // State for sidebar and execution selection - declare early to avoid hoisting issues
  const [selectedExecutionId, setSelectedExecutionId] = useState<string | null>(executionIdFromUrl);
  const [selectedExecution, setSelectedExecution] = useState<SpecializedAgentExecution | null>(
    null
  );
  const [isSidebarOpen, setSidebarOpen] = useState(false);
  const [showValidationErrors, setShowValidationErrors] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [isAgentFormInputOpen, setIsAgentFormInputOpen] = useState(true);
  const [isUnusedDialogOpen, setIsUnusedDialogOpen] = useState(false);
  const [shouldShowUnusedParamsWarning, setShouldShowUnusedParamsWarning] = useState(false);

  const { unusedParams } = useUnusedParameters(
    formData.parameters || [],
    formData.prompt_template || ''
  );

  const navigateToAgentsPage = () => {
    if (isEmbedded && onClose) {
      onClose();
    } else {
      goBackToBackHref();
    }
  };

  useEffect(() => {
    if (detailedAgent && isCopyMode) {
      // In copy mode, transform the data for new agent creation
      const copiedData: Partial<SpecializedAgent> = {
        ...detailedAgent,
        id: '', // Clear ID to ensure new agent creation
        name: `${detailedAgent.name} copy`, // Suffix name
        created_at: '',
        updated_at: '',
        // Clear any other fields that should be reset for copy
      };
      dispatch({ type: 'SET_ALL', payload: copiedData as SpecializedAgent });
    } else if (detailedAgent && isEditing) {
      // In edit mode, use the original data
      dispatch({ type: 'SET_ALL', payload: detailedAgent });
    }
    // Only run when detailedAgent changes or mode changes
  }, [detailedAgent, dispatch, isCopyMode, isEditing]);

  // Function to search for execution directly when opening from URL
  const searchForExecutionDirectly = useCallback(
    async (executionId: string) => {
      if (!instanceId || !formData.id) return;

      try {
        // Try pages 1-5 to find the execution
        for (let page = 1; page <= 5; page++) {
          const pageData = await agentExecutionsService.getAgentExecutions(
            instanceId,
            formData.id,
            page,
            20
          );

          if (pageData?.items) {
            const execution = pageData.items.find(
              (exec: SpecializedAgentExecution) => exec.id === executionId
            );
            if (execution) {
              setSelectedExecution(execution);
              return;
            }
          }
        }

        console.warn('Execution not found in first 5 pages:', executionId);
      } catch (error) {
        console.error('Error searching for execution:', error);
      }
    },
    [instanceId, formData.id]
  );

  // Handle URL changes and update sidebar state accordingly
  useEffect(() => {
    const executionIdFromUrl = searchParams.get('executionId');
    if (executionIdFromUrl) {
      setSelectedExecutionId(executionIdFromUrl);
      setSidebarOpen(true);
      // If we have an execution ID but no execution object, search for it
      if (executionIdFromUrl !== selectedExecution?.id && formData.id) {
        searchForExecutionDirectly(executionIdFromUrl);
      }
    } else {
      setSelectedExecutionId(null);
    }
  }, [searchParams, selectedExecution?.id, formData.id, searchForExecutionDirectly]);

  // Function to update URL with execution ID and execution object
  const handleExecutionSelect = (
    executionId: string | null,
    execution?: SpecializedAgentExecution | null
  ) => {
    if (executionId) {
      searchParams.set('executionId', executionId);
      setSearchParams(searchParams);
      setSelectedExecutionId(executionId);
      if (execution) {
        setSelectedExecution(execution);
      }
      // Close Test Run sidebar when execution is selected to avoid conflict
      setSidebarOpen(false);
    } else {
      searchParams.delete('executionId');
      setSearchParams(searchParams);
      setSelectedExecutionId(null);
      setSelectedExecution(null);
    }
  };

  // Function to close execution details sidebar
  const handleCloseExecutionSidebar = () => {
    handleExecutionSelect(null);
    // Also close the Test Run sidebar to prevent it from automatically opening
    setSidebarOpen(false);
  };

  const { mutate: createAgent, isPending: isCreating } = useCreateSpecializedAgent(
    instanceId ?? '',
    (createdAgent: SpecializedAgent) => {
      dispatch({ type: 'SET_ALL', payload: createdAgent });
      navigate(`/agents/specialized/${createdAgent.id}`, { replace: true });
    }
  );

  const { mutate: updateAgent, isPending: isUpdating } = useUpdateSpecializedAgent(
    agentDetailsUrl || ''
  );

  const { mutate: deleteAgent, isPending: isDeleting } =
    useDeleteSpecializedAgent(navigateToAgentsPage);

  const {
    isCheckingAvailability,
    availabilityStatus,
    idFieldStatus,
    isSaveDisabled: isIdCheckDisabled,
  } = useAgentId(instanceId, formData.agent_id, isEditing, detailedAgent?.agent_id);

  // Check if required fields are filled
  const isRequiredFieldsEmpty =
    !formData.name?.trim() || !formData.agent_id?.trim() || !formData.description?.trim();

  const isSaveDisabled = isIdCheckDisabled || isCreating || isUpdating;

  const executeAction = useCallback(() => {
    if (isEditing) {
      updateAgent(formData);
    } else {
      createAgent(formData);
    }
  }, [isEditing, updateAgent, createAgent, formData]);

  const handleSaveClick = useCallback(() => {
    if (isRequiredFieldsEmpty) {
      setShowValidationErrors(true);
      return;
    }

    if (unusedParams.length > 0) {
      setIsUnusedDialogOpen(true);
      return;
    }

    executeAction();
  }, [isRequiredFieldsEmpty, unusedParams, executeAction]);

  // Show loading spinner if agent details are loading
  if (isAgentLoading && currentAgentListItem) {
    return (
      <Flex alignItems="center" justifyContent="center" style={{ minHeight: '300px' }}>
        <Spinner />
      </Flex>
    );
  }

  return (
    <Flex className={AppPagePadding}>
      <Flex className={clsx(isStickyHeader && styles.stickyBackButton)}>
        <Button
          appearance="subtle"
          icon={<IconArrowLeft size="12" />}
          onClick={isEmbedded && onClose ? onClose : goBackToBackHref}
          w="fit"
        >
          Agents
        </Button>
      </Flex>
      <Tabs
        value={activeTab}
        onValueChange={value => {
          if (value === 'form') {
            searchParams.delete('tab');
            searchParams.delete('page');
          } else {
            searchParams.set('tab', value);
          }
          setSearchParams(searchParams, { replace: true });
        }}
        className={
          isSidebarOpen || !!selectedExecutionId || !!selectedExecution
            ? agentStyles.FormAgentMaxWidthSidebarOpen
            : agentStyles.FormAgentMaxWidth
        }
      >
        <Flex className={isStickyHeader ? styles.stickyTabsListWithHeader : undefined} pt="8">
          <Flex flexDirection="row" justifyContent="space-between" alignItems="center">
            <Flex flexDirection="column" gap="8">
              <Heading level="3" color="fg.default" className={styles.titleHeader} truncate>
                {formData.name || 'Specialized Agent'}
              </Heading>
            </Flex>
            <Flex gap="8" alignItems="center">
              {!isEmbedded && (
                <AgentActionButtons
                  type="specialized"
                  agentId={formData.id}
                  isTestRunDisabled={!formData.id}
                  isEditing={isEditing}
                  isSaveDisabled={isSaveDisabled}
                  isCreating={isCreating}
                  isUpdating={isUpdating}
                  onSave={handleSaveClick}
                  isSidebarOpen={isSidebarOpen || !!selectedExecutionId || !!selectedExecution}
                  onTestRun={() => {
                    setSidebarOpen(true);
                    handleExecutionSelect(null);
                  }}
                  onDelete={() => setDeleteDialogOpen(true)}
                />
              )}
              {isEmbedded && onClose && (
                <Button
                  appearance="subtle"
                  size="sm"
                  onClick={onClose}
                  aria-label="Close SA creation"
                >
                  <IconXmark />
                </Button>
              )}
            </Flex>
          </Flex>
          <TabsList>
            <TabsTrigger value="form">Setup</TabsTrigger>
            {formData.id && hasAppCompanionFF && (
              <TabsTrigger value="app-companion">In-app Triggers</TabsTrigger>
            )}
            {formData.id && <TabsTrigger value="execution">Logs</TabsTrigger>}
          </TabsList>
        </Flex>

        <TabsContent py="24" value="form">
          <Flex flexDirection="column" gap="24">
            <AgentBasicFields
              lockedFields={isCopyMode ? [] : detailedAgent?.locked_fields || []}
              isInstalled={isCopyMode ? false : !!detailedAgent?.is_installed}
              name={formData.name || ''}
              isActive={formData.is_active}
              agentId={formData.agent_id}
              agentType="specialized"
              idFieldStatus={idFieldStatus}
              isCheckingAvailability={isCheckingAvailability}
              availabilityStatus={availabilityStatus}
              description={formData.description || ''}
              isEnabledInChat={formData.is_enabled_in_chat}
              onChange={(field, value) => {
                dispatch({
                  type: 'UPDATE_FIELD',
                  field: field as keyof SpecializedAgent,
                  value: value as SpecializedAgent[keyof SpecializedAgent],
                });
                // Clear validation errors when user starts fixing the fields
                if (showValidationErrors) {
                  setShowValidationErrors(false);
                }
              }}
              onSuggestionClick={suggestion =>
                dispatch({ type: 'UPDATE_FIELD', field: 'agent_id', value: suggestion })
              }
              showErrors={showValidationErrors}
            />
            <AgentFormInput
              lockedFields={isCopyMode ? [] : detailedAgent?.locked_fields || []}
              formData={formData}
              dispatch={dispatch}
              open={isAgentFormInputOpen}
              onOpenChange={setIsAgentFormInputOpen}
              shouldLoadPromptTemplate={isEditing || isCopyMode}
              shouldShowUnusedParamsWarning={shouldShowUnusedParamsWarning}
            />
            <AgentFormOutput
              formData={formData}
              dispatch={dispatch}
              lockedFields={isCopyMode ? [] : detailedAgent?.locked_fields || []}
            />

            <Separator />

            <Text color="fg.error.hovered">* Required fields</Text>
          </Flex>
        </TabsContent>

        {formData.id && (
          <TabsContent py="24" value="execution">
            <SpecializedAgentExecutionsTab
              agentId={formData.id}
              selectedExecutionId={selectedExecutionId}
              onExecutionSelect={(executionId, execution) =>
                handleExecutionSelect(executionId, execution)
              }
              baselineEvaluationScore={formData.baseline_evaluation_score}
            />
          </TabsContent>
        )}

        {formData.id && hasAppCompanionFF && (
          <FeatureFlag featureKey="app_companion">
            <TabsContent value="app-companion">
              <AppCompanionTab agentId={formData.id || ''} />
            </TabsContent>
          </FeatureFlag>
        )}
      </Tabs>

      <Sidebar open={isSidebarOpen && !selectedExecutionId} dock="right" initialWidth={450}>
        <AgentExecutionForm
          agent={formData}
          hideRightSidebar={() => {
            setSidebarOpen(false);
            handleExecutionSelect(null);
          }}
        />
      </Sidebar>

      {/* Execution Details Sidebar - independent of disclosure state */}
      <Sidebar open={!!selectedExecution || !!selectedExecutionId} dock="right" initialWidth={450}>
        {(selectedExecution || selectedExecutionId) && (
          <SpecializedAgentExecutionDetailsSidebar
            executionId={selectedExecution?.id || selectedExecutionId || ''}
            onClose={handleCloseExecutionSidebar}
          />
        )}
      </Sidebar>

      <ConfirmationModal
        isShown={deleteDialogOpen}
        title="Delete Agent"
        action="Delete"
        appearance="danger"
        onCancel={() => setDeleteDialogOpen(false)}
        onConfirm={() => {
          const deleteUrl = detailedAgent?.links?.delete || currentAgentListItem?.links.delete;
          if (deleteUrl) {
            deleteAgent(deleteUrl);
          }
          setDeleteDialogOpen(false);
        }}
        confirmLoading={isDeleting}
        cancelDisabled={isDeleting}
      >
        <Flex flexDirection="column" gap="4">
          <Text>
            You are about to permanently delete the agent{' '}
            <Text fontWeight="600">{formData.name}</Text>
          </Text>
          <Text>Do you want to continue?</Text>
        </Flex>
      </ConfirmationModal>

      <ConfirmationModal
        isShown={isUnusedDialogOpen}
        title="Unused variables found"
        onCancel={() => {
          setIsUnusedDialogOpen(false);
          setIsAgentFormInputOpen(true);
          setShouldShowUnusedParamsWarning(true);
        }}
        onConfirm={() => {
          setIsUnusedDialogOpen(false);
          executeAction();
          setShouldShowUnusedParamsWarning(true);
        }}
        cancelLabel="Go back and review"
        action="Save anyway"
        appearance="primary"
        size="md"
      >
        <Flex flexDirection="column" gap="16">
          <Text>The following variables should be referenced in prompt template.</Text>
          {unusedParams.length > 0 && (
            <Flex flexDirection="column" gap="8">
              {unusedParams.map((param: AgentParameter<AgentParameterType>) => (
                <VariableRow key={param.name} variable={param} onDelete={() => {}} isReadOnly />
              ))}
            </Flex>
          )}
        </Flex>
      </ConfirmationModal>
    </Flex>
  );
}
